import { useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Configure, InstantSearch } from 'react-instantsearch';
import { useIntl } from 'react-intl';

import { Flex } from '@/components/Flex';
import { LegacyButton } from '@/components/LegacyButton';
import { Typography } from '@/components/Typography';
import { APOLLO_CLIENTS } from '@/graphql/client';
import type { OrderAppProductVariantsBusinessDetailsQuery } from '@/graphql/orderApp/api';
import { useProductVariantsBusinessDetailsQuery } from '@/graphql/orderApp/api';
import type {
  ProductCardsQuery,
  ShoppingBagStrapsModalQuery,
} from '@/graphql/saleor/api';
import { useShoppingBagStrapsModalQuery } from '@/graphql/saleor/api';
import { useAddToBagFromModal } from '@/hooks/useAddToBagFromModal';
import type { AllSelectedProducts } from '@/hooks/useAllSelectedProductsState';
import { useAllSelectedProductsState } from '@/hooks/useAllSelectedProductsState';
import { useCheckout } from '@/hooks/useCheckout';
import { algoliaSearchClient } from '@/integrations/algolia/client';
import { getIndexName } from '@/integrations/algolia/helpers';
import { STRAP_STANDARD_SIZE_ATTRIBUTE } from '@/integrations/saleor/consts';
import { getMultiselectValue } from '@/lib/attributes';
import {
  NEXT_PUBLIC_IMAGES_FORMAT,
  SORTEDSTRAPSIZES,
  THUMBNAIL_IMAGE_SIZE,
} from '@/lib/const';
import { mapEdgesToItems } from '@/lib/maps';
import { getTaxMessageDetail } from '@/lib/regions';
import { commonMessages } from '@/messages/common';
import { LocaleContext } from '@/providers/LocaleProvider';
import { useRegionProvider } from '@/providers/RegionProvider';
import { getTranslatedStrapNames } from '@/views/ShoppingBagView/components/ShoppingBagStrapDrawer/helpers';
import type { ShoppingBagStrapModalProps } from '@/views/ShoppingBagView/components/ShoppingBagStrapModal';

import { Icon } from '../../../../components/Icon';
import { Loader } from '../../../../components/Loader';
import { ProductItem, ShoppingBagStrapDetails } from './components';
import { StrapDetailSearch } from './components/StrapDetailSearch/StrapDetailSearch';

import * as S from './ShoppingBagStrapDrawer.styles';

const initialState: AllSelectedProducts = {
  products: [],
  totalPrice: {
    currency: '',
    gross: {
      amount: 0,
      currency: '',
    },
    net: {
      amount: 0,
      currency: '',
    },
    tax: {
      amount: 0,
      currency: '',
    },
  },
};

export type StrapAddToBagStrap = NonNullable<
  NonNullable<
    ShoppingBagStrapsModalQuery['products']
  >['edges'][number]['node']['defaultVariant']
>;

export type ProductData = NonNullable<
  ProductCardsQuery['products']
>['edges'][number]['node'];

export function getValidStrap(
  item: any,
  orderAppData: OrderAppProductVariantsBusinessDetailsQuery | null,
  orderCtaType: string,
): any {
  const isStandardValid = orderAppData?.productVariants?.filter(
    (jtem) => jtem.sku === item?.sku && jtem.variantCTA === orderCtaType,
  );

  if (isStandardValid?.length) return item;

  const sortedOtherSizes = sortOtherSizes(
    item?.product.otherSizesSkus?.values || [],
  );
  return { ...item, sku: sortedOtherSizes[0]?.name };
}

function sortOtherSizes(
  otherSizes: { name: string; slug: string }[],
): { name: string; slug: string }[] {
  return [...otherSizes].sort((a, b) => {
    const sizeA = (a.slug as string).toLowerCase().replace(/\d+/, '');
    const sizeB = (b.slug as string).toLowerCase().replace(/\d+/, '');
    return SORTEDSTRAPSIZES.indexOf(sizeA) - SORTEDSTRAPSIZES.indexOf(sizeB);
  });
}

export const ShoppingBagStrapDrawer = ({
  channel,
  compatibleStrapIds,
  onClose,
  orderCtaType,
  watch,
}: ShoppingBagStrapModalProps) => {
  const modalTargetElement = useRef<HTMLElement | null>(null);
  const {
    query: { channel: channelSlug, languageCode },
  } = useRegionProvider();
  const taxMessage = getTaxMessageDetail(channelSlug);
  const locale = useContext(LocaleContext);
  useEffect(() => {
    modalTargetElement.current = document.getElementById('modal-root');
  }, []);

  const { formatMessage } = useIntl();
  const { query } = useRegionProvider();
  const [translatedStrapNames, setTranslatedStrapNames] = useState<
    Record<string, string>
  >({});
  const {
    snapshot: { isProcessing },
  } = useCheckout();

  const { allSelectedProducts, setAllSelectedProducts } =
    useAllSelectedProductsState(initialState);
  const { addToBagFromModal } = useAddToBagFromModal(
    allSelectedProducts,
    watch?.checkoutLineId,
  );

  const [selectedStrap, setSelectedStrap] = useState<
    undefined | StrapAddToBagStrap
  >(undefined);
  const [selectedStrapSku, setSelectedStrapSku] = useState<null | string>(null);
  const [straps, setStraps] = useState<StrapAddToBagStrap[]>([]);

  const { data, loading: dataLoading } = useShoppingBagStrapsModalQuery({
    fetchPolicy: 'cache-first',
    skip: !watch || !compatibleStrapIds.length,
    variables: {
      filter: {
        slugs: compatibleStrapIds,
        attributes: [STRAP_STANDARD_SIZE_ATTRIBUTE],
      },
      thumbnailSize: THUMBNAIL_IMAGE_SIZE,
      imagesFormat: NEXT_PUBLIC_IMAGES_FORMAT,
      channel: query.channel,
      languageCode: query.languageCode,
    },
  });

  const businessSkus: string[] = [...compatibleStrapIds];
  data?.products?.edges.forEach((item) => {
    businessSkus.push(
      ...getMultiselectValue(item.node.defaultVariant?.product.otherSizesSkus),
    );
  });
  const { data: orderAppData, loading: ctaLoading } =
    useProductVariantsBusinessDetailsQuery({
      context: {
        client: APOLLO_CLIENTS.orderApp,
      },
      fetchPolicy: 'network-only',
      skip: !businessSkus.length || !watch,
      variables: { skus: businessSkus, channel: query.channel },
    });

  const strapSkus: string[] = straps
    .map((strap) => strap.sku)
    .filter((sku): sku is string => typeof sku === 'string');
  useEffect(() => {
    if (
      strapSkus.length > 0 &&
      Object.keys(translatedStrapNames).length === 0
    ) {
      getTranslatedStrapNames(strapSkus, languageCode).then(
        setTranslatedStrapNames,
      );
    }
  }, [strapSkus, channelSlug, languageCode]);
  const loading = dataLoading || ctaLoading;

  const close = () => {
    setAllSelectedProducts(initialState);
    setSelectedStrap(undefined);
    setSelectedStrapSku(null);
    onClose?.();
  };

  const addToBag = async () => {
    await addToBagFromModal();
    close();
  };

  useEffect(() => {
    if (data && orderAppData) {
      const strapsList: StrapAddToBagStrap[] = getFilteredStraps(
        data,
        orderAppData,
        orderCtaType,
      );
      setStraps(strapsList);
    }
  }, [loading]);

  function getFilteredStraps(
    data: ShoppingBagStrapsModalQuery,
    orderAppData: OrderAppProductVariantsBusinessDetailsQuery,
    orderCtaType: string,
  ): StrapAddToBagStrap[] {
    return mapEdgesToItems(data?.products)
      .map((item) => item.defaultVariant)
      .filter((item) => isStrapAvailable(item, orderAppData, orderCtaType))
      .map((item) => getValidStrap(item, orderAppData, orderCtaType));
  }

  function isStrapAvailable(
    item: any,
    orderAppData: OrderAppProductVariantsBusinessDetailsQuery | null,
    orderCtaType: string,
  ): boolean {
    const ctaArr = orderAppData?.productVariants?.filter((jtem) => {
      return (
        jtem.sku === item?.sku ||
        getMultiselectValue(item?.product.otherSizesSkus).indexOf(jtem.sku) > -1
      );
    });

    for (let i = 0; i < (ctaArr?.length || 0); i++) {
      if (
        ctaArr![i].variantCTA === orderCtaType &&
        ctaArr![i].quantityAvailable > 0
      ) {
        return true;
      }
    }
    return false;
  }

  const updateStraps = () => {
    setStraps((prevStraps) =>
      prevStraps.map((strap) => {
        if (strap.id === selectedStrap?.id) {
          return { ...strap, sku: selectedStrapSku };
        }
        return strap;
      }),
    );
  };

  const updateSelectedStrapSku = (sku: string) => {
    setSelectedStrapSku(sku);
  };

  useEffect(() => {
    updateStraps();
  }, [selectedStrapSku]);

  const portalContent = (
    <S.DrawerOverlay
      data-test-id='ShoppingBagStrapDrawerOverlay'
      onClick={close}
    >
      <S.Wrapper>
        <S.HeaderWrapper data-test-id='ShoppingBagStrapDrawerHeaderWrapper'>
          <S.CloseButton
            data-test-id='ShoppingBagStrapDrawerCloseButton'
            onClick={close}
          >
            <Icon
              data-test-id='ShoppingBagStrapDrawerIcon'
              name='chevronLeft'
              size='m'
              variant='regular'
            />
          </S.CloseButton>
          <S.Header data-test-id='ShoppingBagStrapDrawerHeader'>
            <S.HeaderText data-test-id='ShoppingBagStrapDrawerHeaderText'>
              {formatMessage(commonMessages.addAnAdditionalStrap)}
            </S.HeaderText>
          </S.Header>
        </S.HeaderWrapper>

        <S.Body data-test-id='ShoppingBagStrapDrawerBody'>
          {!loading ? (
            straps.length ? (
              straps.map((strap, index) => (
                <InstantSearch
                  key={strap.sku}
                  indexName={getIndexName('PRODUCT', locale!)}
                  insights={false}
                  searchClient={algoliaSearchClient}
                >
                  <Configure
                    distinct={true}
                    filters={`objectID:${strap.sku as string}`}
                    restrictSearchableAttributes={['objectID']}
                  />
                  <StrapDetailSearch key={strap.sku} sku={strap.sku as string}>
                    <ProductItem
                      key={strap.sku}
                      data={strap}
                      data-test-id={`ShoppingBagStrapDrawerProductItem-${index}`}
                      isClickable
                      isDeselectable={false}
                      onClick={() => {
                        setSelectedStrap(strap);
                      }}
                      productType='strap'
                      selectedSku={selectedStrapSku}
                      setAllSelectedProducts={setAllSelectedProducts}
                      setSelectedSku={setSelectedStrapSku}
                      translatedStrapNames={translatedStrapNames}
                    />{' '}
                  </StrapDetailSearch>
                </InstantSearch>
              ))
            ) : (
              <Flex alignItems='center'>
                <Typography data-test-id='ShoppingBagStrapDrawerSTrapsOutOfStock'>
                  Straps are out of stock
                </Typography>
              </Flex>
            )
          ) : (
            <Loader
              data-test-id='ShoppingBagStrapDrawerLoader'
              height='fullHeight'
            />
          )}
          {selectedStrap && (
            <ShoppingBagStrapDetails
              allSelectedProducts={allSelectedProducts}
              data-test-id='ShoppingBagStrapDrawerDetails'
              orderAppData={orderAppData}
              orderCtaType={orderCtaType}
              selectedStrapSku={selectedStrapSku}
              setAllSelectedProducts={setAllSelectedProducts}
              strap={selectedStrap as StrapAddToBagStrap}
              updateSelectedStrapSku={updateSelectedStrapSku}
            />
          )}
          <S.BottomTextWrapper>
            <ul>
              <li>
                <Typography>
                  {taxMessage &&
                    formatMessage(commonMessages.priceVatNoticePart1, {
                      taxMessage: formatMessage(taxMessage),
                    })}
                </Typography>
              </li>
              <li>
                <Typography>
                  {formatMessage(commonMessages.priceVatNoticePart2)}
                </Typography>
              </li>
            </ul>
          </S.BottomTextWrapper>
        </S.Body>

        <S.Footer data-test-id='ShoppingBagStrapDrawerFooter'>
          {selectedStrap && (
            <>
              <S.TotalWrapper data-test-id='ShoppingBagStrapDrawerTotalWrapper'>
                <S.TotalLabel data-test-id='ShoppingBagStrapDrawerTotalLabel'>
                  {formatMessage(commonMessages.total)}
                </S.TotalLabel>
                <S.TotalValue
                  channel={channel}
                  data-test-id='ShoppingBagStrapDrawerTotalValue'
                  displayZero={true}
                  price={allSelectedProducts.totalPrice}
                />
              </S.TotalWrapper>
              <LegacyButton
                data-test-id='ShoppingBagStrapDrawerLegacyButton'
                isFluid
                isProcessing={isProcessing}
                label={formatMessage(commonMessages.addToBag)}
                onClick={addToBag}
              />
            </>
          )}
        </S.Footer>
      </S.Wrapper>
    </S.DrawerOverlay>
  );

  if (!modalTargetElement.current || !watch) {
    return null;
  }

  return createPortal(portalContent, modalTargetElement.current);
};

ShoppingBagStrapDrawer.displayName = 'ShoppingBagStrapDrawer';
